<script setup lang="ts">
/**
 * BaseCombobox — 검색형 단일 선택(reka-ui Combobox 프리미티브, 059 US5).
 *
 * 네이티브 `<select>`(BaseSelect)는 검색이 구조적으로 불가능하다 — 선택지가 수십 개를 넘고
 * 부분 일치 탐색이 필요한 곳(대학 필터 등)은 이 컴포넌트를 쓴다. 옵션 그룹(예: 보유 대학
 * 상단 배치)을 지원하고, 검색은 라벨 부분 일치(reka-ui 기본 필터 — textValue 기준)다.
 *
 * 시각은 BaseField/BaseSelect 와 동일 골격(fieldStyles SSoT) — 색은 토큰 경유.
 */
import { ChevronDown, X } from 'lucide-vue-next';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui';
import { computed } from 'vue';

import { FIELD_CONTROL_BASE_CLASS } from '#/lib/fieldStyles';
import { cn } from '#/lib/utils';

defineOptions({ name: 'BaseCombobox' });

export interface ComboboxOption {
  label: string;
  value: string;
}

/** 옵션 그룹 — `label: null` 은 머리글 없는 그룹(단일 그룹 목록용). */
export interface ComboboxOptionGroup {
  label: string | null;
  options: ComboboxOption[];
}

interface Props {
  /** v-model 값. '' = 미선택. */
  modelValue?: string;
  /** 옵션 그룹(순서 유지 — 우선 그룹을 앞에 둔다). */
  groups?: ComboboxOptionGroup[];
  /** 미선택 시 입력 placeholder. */
  placeholder?: string;
  /** 접근성 라벨(시각 라벨이 없는 필터 바 용도). */
  ariaLabel?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  groups: () => [],
  placeholder: '',
  ariaLabel: undefined,
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

/** value → label 역인덱스 — 선택값 표시(displayValue)용. */
const labelByValue = computed(() => {
  const map = new Map<string, string>();
  for (const group of props.groups) {
    for (const opt of group.options) map.set(opt.value, opt.label);
  }
  return map;
});

function displayValue(value: unknown): string {
  return typeof value === 'string' ? (labelByValue.value.get(value) ?? '') : '';
}

/** reka-ui 는 해제 시 null 을 줄 수 있다 — 외부 계약은 '' 로 고정한다. */
function onSelect(value: unknown): void {
  emit('update:modelValue', typeof value === 'string' ? value : '');
}

function clear(): void {
  emit('update:modelValue', '');
}

const inputClass = cn(FIELD_CONTROL_BASE_CLASS, 'py-3 pl-[14px] pr-[64px]');
</script>

<template>
  <ComboboxRoot
    :model-value="modelValue === '' ? undefined : modelValue"
    :disabled="disabled"
    open-on-click
    @update:model-value="onSelect"
  >
    <ComboboxAnchor class="relative w-full">
      <ComboboxInput
        :display-value="displayValue"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :class="inputClass"
      />
      <!-- 클리어 — 선택 해제('' = 전체). 필터 용도라 항상 허용한다 -->
      <button
        v-if="modelValue !== ''"
        type="button"
        class="absolute right-8 top-1/2 grid h-6 w-6 -translate-y-1/2 place-content-center rounded text-muted transition-colors hover:text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        aria-label="선택 해제"
        @click.stop="clear"
      >
        <X :size="14" :stroke-width="2.2" />
      </button>
      <ComboboxTrigger
        class="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-content-center text-muted"
        aria-label="선택지 열기"
        :disabled="disabled"
      >
        <ChevronDown :size="16" aria-hidden="true" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent
      position="popper"
      :side-offset="4"
      class="z-modal w-[var(--reka-popper-anchor-width)] overflow-hidden rounded-field border border-line bg-white shadow-md"
    >
      <ComboboxViewport class="max-h-[280px] overflow-y-auto p-1">
        <ComboboxEmpty class="px-3 py-3 text-center text-[12.5px] text-muted">
          검색 결과가 없습니다
        </ComboboxEmpty>
        <ComboboxGroup v-for="(group, gi) in groups" :key="group.label ?? gi">
          <ComboboxLabel
            v-if="group.label"
            class="px-2.5 pb-1 pt-2 text-[11px] font-bold tracking-[0.02em] text-muted"
          >
            {{ group.label }}
          </ComboboxLabel>
          <ComboboxItem
            v-for="opt in group.options"
            :key="opt.value"
            :value="opt.value"
            :text-value="opt.label"
            class="cursor-pointer rounded-md px-2.5 py-2 text-[13.5px] text-body transition-colors data-[highlighted]:bg-bg-2 data-[state=checked]:font-bold data-[state=checked]:text-brand"
          >
            {{ opt.label }}
          </ComboboxItem>
        </ComboboxGroup>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>
